import {useState, useEffect} from 'react';
import {Divider, Input, Card, Checkbox, Button, Row} from 'antd';
import {getDestroyedPlants, getMomPlants, getPlants, getTaggedPlants, getTags, insertTag} from "../../api/Api";
import {PlantTable} from "./PlantTable";
import {PlantStageType} from "../../api/Constants";
import {handleNoSession} from "../../util/errorUtils";
import {PlantTagWidget} from "./PlantTagWidget";
import {TagSelectWidget} from "./TagSelectWidget";

export const PlantSearch = ({roomMap, refresh, onRefresh, onApiFail, props}) => {
    const [searchVal, setSearchVal] = useState(undefined);
    const [plants, setPlants] = useState([]);
    const [filteredPlants, setFilteredPlants] = useState([]);
    const [moms, setMoms] = useState([]);
    const [filteredMoms, setFilteredMoms] = useState([]);
    const [checkDestroyed, setCheckDestroyed] = useState(false)
    const [tagVal, setTagVal] = useState(undefined);
    const [tagMap, setTagMap] = useState({});
    const [filteredTags, setFilteredTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState(undefined);
    const [taggedPlantMap, setTaggedPlantMap] = useState(undefined);

    // check selection state
    const [checked, setChecked] = useState({});

    useEffect(() => {
        if (checkDestroyed === true) {
            getDestroyedPlants().then(r => {
                setPlants(r.map(row => {
                    return {
                        ...row,
                        key: row.id
                    }
                }))
                // console.log(r);
            }).catch(e => {
                handleNoSession(e, onApiFail)
            });
        } else {
            getPlants().then(r => {
                setPlants(r.map(row => {
                    return {
                        ...row,
                        key: row.id
                    }
                }));
                // console.log(r);
            }).catch(e => {
                handleNoSession(e, onApiFail)
            });

            getMomPlants().then(r => {
                setMoms(r.map(row => {
                    return {
                        ...row,
                        key: row.id
                    }
                }))
                // console.log(r);
            }).catch(e => {
                handleNoSession(e, onApiFail)
            });
        }
    }, [checkDestroyed, onApiFail]);

    useEffect(() => {
        getTags().then(r => {
            const nextMap = {}
            r.forEach(tag => {
                const {name} = tag;
                nextMap[name] = {
                    ...tag,
                    key: tag.id
                }
            })
            setTagMap(nextMap);
            setFilteredTags(Object.keys(nextMap))
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }, [onApiFail, refresh]);

    useEffect(() => {
        const taggedPlants = (taggedPlantMap !== undefined) ? plants.filter(p => {
            return taggedPlantMap[p.id] !== undefined;
        }) : plants;
        if (searchVal === undefined) {
            setFilteredPlants(taggedPlants);
            return;
        }
        const nextResults = taggedPlants.filter(p => {
            const {name, metricTag} = p;
            const search = searchVal.toLowerCase();
            let metricSearch = (metricTag) ?  metricTag.toLowerCase().includes(search) : false;
            return (name.toLowerCase().includes(search)) || metricSearch;
        });
        setFilteredPlants (nextResults);
        // clear checked everytime our search changes
        setChecked({})
    }, [plants, searchVal, taggedPlantMap]);

    useEffect(() => {
        const taggedMoms = (taggedPlantMap !== undefined) ? moms.filter(m => {
            return taggedPlantMap[m.id] !== undefined
        }) : moms;
        if (searchVal === undefined) {
            setFilteredMoms(taggedMoms);
            return;
        }
        const nextResults = taggedMoms.filter(p => {
            const {name, metricTag} = p;
            const search = searchVal.toLowerCase();
            let metricSearch = (metricTag) ?  metricTag.toLowerCase().includes(search) : false;
            return (name.toLowerCase().includes(search)) || metricSearch;
        });
        setFilteredMoms (nextResults);
        // clear checked everytime our search changes
        setChecked({})
    }, [moms, searchVal, taggedPlantMap]);

    useEffect(() => {
        let nextTags = Object.keys(tagMap)
        if (tagVal === undefined) {
            setFilteredTags(nextTags)
            return;
        }
        nextTags = nextTags.filter(tagName => {
            return tagName.toLowerCase().includes(tagVal.toLowerCase());
        })

        setFilteredTags(nextTags);
    }, [tagMap, tagVal])

    const onCheckDestroyedChanged = (e) => {
        setCheckDestroyed(e.target.checked);
    }

    const onSearchChange = (e) => {
        setSearchVal(e.target.value);
    }

    const onTagChange = (e) => {
        setTagVal(e.target.value);
    }

    const onNewTag = () => {
        insertTag(tagVal).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const clearTagFilter = () => {
        setSelectedTag(undefined);
        setTaggedPlantMap(undefined)
        // when we clear this filter make everything unchecked
        setChecked({})
    }

    const defStyle = {
        textAlign: 'left'
    }

    const rowStyle = {
        marginTop: '10px',
        marginBottom: '10px'
    }


    const filterLabel = selectedTag ? `with tag ${selectedTag}` : ""
    let plantTitle;
    let plantStage;
    if (checkDestroyed) {
        plantTitle = `Destroyed Plants ${filterLabel}`
        plantStage = PlantStageType.Destroyed;
    } else {
        plantTitle = `Clones ${filterLabel}`
        // any clone stage will work
        plantStage = PlantStageType.Veg;
    }

    const newTagDisabled = (tagVal === undefined) || (tagVal.trim() === "") || (tagMap[tagVal] !== undefined);

    const onTagClick = (tag) => () => {
        // console.log(`click ${tag.name}`)
        // when we click a tag, filter moms and plants by the tag
        getTaggedPlants(tag.id).then(r => {
            const taggedPlantsMap = {};
            r.forEach(tag => {
                const {plantId} = tag;
                taggedPlantsMap[plantId] = plantId;
            });
            // console.log(`filtering by tag`);
            // console.log(taggedPlantsMap);
            setSelectedTag(tag.name);
            setTaggedPlantMap(taggedPlantsMap);
            // when we add this tag filter clear everyting that's checked
            setChecked({});
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }

    const tablePagination = {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: [50, 100, 150, 200]
    }

    const tagList = filteredTags.map((f, index) => {
        const t = tagMap[f];
        return <PlantTagWidget key={f} popOver={`Filter by Tag ${f}`} tag={t} onClick={onTagClick(t)} />
    })

    let itemsChecked = false;
    for (const value of Object.values(checked)) {
        if (value === true) {
            itemsChecked = true;
        }
    }
    return <div style={defStyle}>
        <Card title={<b>Search</b>}>
            <Input placeholder={'Search'} value={searchVal} onChange={onSearchChange} />
            <Divider />
            <Checkbox checked={checkDestroyed} onChange={onCheckDestroyedChanged}><b>Search Destroyed</b></Checkbox>
        </Card>
        <Card title={<b>Tags</b>}>
            <Row style={rowStyle}>
            <Input placeholder={'Tag'} value={tagVal} onChange={onTagChange} />
            </Row>
            <Row style={rowStyle}>
            <Button onClick={onNewTag} disabled={newTagDisabled}>New Tag</Button>
            </Row>
            <Divider />
            <Row style={rowStyle}>
            {
                tagList
            }
            </Row>
            {(selectedTag !== undefined) &&
                <Button style={{marginRight: '10px'}} onClick={clearTagFilter}>Clear Tag Filter</Button>
            }
            {(itemsChecked) &&
                <TagSelectWidget plantId={Object.keys(checked)} onApiFail={onApiFail} refresh={refresh} onRefresh={onRefresh} disabled={!itemsChecked} label={`Tag Selection`}/>
            }
        </Card>
        {(checkDestroyed === false) &&
        <Card title={<b>{`Moms ${filterLabel}`}</b>}>
            <PlantTable plants={filteredMoms} stage={PlantStageType.Mom} roomMap={roomMap} pagination={tablePagination} checked={checked} setChecked={setChecked}/>
        </Card>
        }

        <Card title={<b>{plantTitle}</b>}>
            <PlantTable plants={filteredPlants} stage={plantStage} roomMap={roomMap}pagination={tablePagination}  checked={checked} setChecked={setChecked}/>
        </Card>
    </div>
}